/**
 * useUpcomingAgendaCount
 *
 * Telt alleen calendarEvents die zijn aangemaakt NA het laatste bezoek
 * aan de agenda-pagina. Zo geeft de badge een accurate indicatie van
 * �cht nieuwe items die de gebruiker nog niet heeft gezien.
 *
 * Werking:
 *  - Leest 'lastViewedAgendaAt' uit localStorage (timestamp ms).
 *  - Telt events waar:
 *      - gebruiker participant is
 *      - createdAt > lastViewedAgendaAt
 *      - startTime >= nu  (geen afgelopen events meetellen)
 *  - Wordt live bijgehouden via onSnapshot.
 *  - Sloot de count op 0 terwijl activePage === 'agenda'.
 *
 * Gebruik in App.tsx:
 *  const { count, markAsSeen } = useUpcomingAgendaCount(uid);
 *  // Roep markAsSeen() aan zodra de gebruiker naar 'agenda' navigeert.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

const LS_KEY = 'lastViewedAgendaAt';

function getLastViewed(): number {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return Number(raw);
  } catch {}
  // Eerste keer: stel in op "nu" zodat bestaande events niet direct tellen
  const now = Date.now();
  try { localStorage.setItem(LS_KEY, String(now)); } catch {}
  return now;
}

const useUpcomingAgendaCount = (uid: string | undefined): {
  count: number;
  markAsSeen: () => void;
} => {
  const [count, setCount] = useState(0);
  // Houd de lastViewed waarde bij in een ref zodat onSnapshot er altijd
  // de meest recente versie van ziet zonder her-abonneren.
  const lastViewedRef = useRef<number>(getLastViewed());

  const markAsSeen = useCallback(() => {
    const now = Date.now();
    try { localStorage.setItem(LS_KEY, String(now)); } catch {}
    lastViewedRef.current = now;
    setCount(0); // direct op 0 zetten zonder te wachten op Firestore
  }, []);

  useEffect(() => {
    if (!uid) { setCount(0); return; }

    const nowTs = Timestamp.now();
    // We filteren op startTime >= nu om oude afspraken uit te sluiten.
    // De createdAt-check doen we client-side (Firestore staat geen twee
    // inequality-filters op verschillende velden toe in hetzelfde query).
    const q = query(
      collection(db, 'calendarEvents'),
      where('participants', 'array-contains', uid),
      where('startTime', '>=', nowTs),
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const lastViewed = lastViewedRef.current;
        let newCount = 0;
        snap.docs.forEach((d) => {
          const data = d.data();
          // createdAt kan Timestamp of number zijn
          const createdMs =
            data.createdAt?.toMillis?.() ??
            (typeof data.createdAt === 'number' ? data.createdAt : 0);
          if (createdMs > lastViewed) {
            newCount++;
          }
        });
        setCount(newCount);
      },
      () => setCount(0),
    );

    return () => unsub();
  }, [uid]);

  return { count, markAsSeen };
};

export { useUpcomingAgendaCount };
export default useUpcomingAgendaCount;
